import React from 'react';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  Globe, Sparkles, Languages, Calculator, Camera, MapPin, 
  Keyboard, Brain, Wand2, RefreshCw, ListOrdered, MessageSquare,
  Smartphone, Film, PartyPopper, Briefcase, Mail, Target,
  HelpCircle, Quote, AtSign, ShoppingCart, Video, Users, Home,
  Check, X, Minus, Download
} from 'lucide-react';

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.typeit4u.keyboard";

const FeaturesPage = () => {
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const coreFeatures = [
    {
      icon: Globe,
      title: "All 22 Indian Languages",
      description: "Every constitutionally scheduled Indian language supported — Tamil, Hindi, Telugu, Bengali, Kannada, Malayalam, Gujarati, Punjabi, Marathi, and 13 more. Type in Roman letters, see native script.",
      badge: "EXCLUSIVE"
    },
    {
      icon: Sparkles,
      title: "Bilingual Auto-Detection",
      description: "Mix languages freely. TypeIt4U detects whether you're writing English or your native language mid-sentence and adjusts suggestions automatically. No toggle. No interruption.",
      badge: "EXCLUSIVE"
    },
    {
      icon: Wand2,
      title: "385 Indian Auto-Corrections",
      description: "Fixes the way India actually types: \"wfh\" → \"working from home\", \"thanku\" → \"thank you\", \"kindly note that\", \"as per our discussion\", and hundreds more professional phrases.",
      badge: "EXCLUSIVE"
    },
    {
      icon: PartyPopper,
      title: "Festival Greeting Calendar",
      description: "TypeIt4U knows Indian festivals. Within 3 days of Diwali, Pongal, Holi, Onam, Ganesh Chaturthi, Navratri, Christmas — it surfaces ready-to-send greeting messages.",
      badge: "NEW 2025"
    },
    {
      icon: Languages,
      title: "Transliterate Whole Field",
      description: "Typed your whole message in English phonetics? One tap converts the entire field word-by-word into native script. Works across all 22 languages.",
      badge: "NEW 2025"
    },
    {
      icon: Calculator,
      title: "Built-in Calculator",
      description: "Type \"2+3*4=\" or \"(10-2)/4=\" directly while typing — TypeIt4U evaluates it and replaces with the answer. No app switch. No copy-paste.",
      badge: "EXCLUSIVE"
    },
    {
      icon: Camera,
      title: "Scan → Type (OCR)",
      description: "Point your camera at any text — signboards, menus, documents, screenshots — and TypeIt4U reads it and types it for you. Built into the keyboard.",
      badge: "EXCLUSIVE"
    },
    {
      icon: MapPin,
      title: "One-Tap Location Insert",
      description: "Need to share where you are? One tap inserts your current GPS address directly into any text field. Perfect for delivery, meetups, and forms.",
      badge: "EXCLUSIVE"
    },
    {
      icon: Keyboard,
      title: "Swipe Typing",
      description: "Slide your finger across letters without lifting. TypeIt4U figures out the word using shape matching and frequency ranking. Live preview shows your word candidate.",
      badge: "FREE"
    }
  ];

  const freeAIModes = [
    { icon: Brain, title: "Answer AI", description: "Type any question → get a direct answer in your field. Like having Google inside your keyboard." },
    { icon: Wand2, title: "Improve", description: "Makes your message clearer and more professional. Your words, polished." },
    { icon: RefreshCw, title: "Rephrase", description: "Rewrites the same idea in a fresh way. Great for social media and avoiding repetition." },
    { icon: Globe, title: "Translate", description: "Pick your target language from a list. Translates selected text, full field, or clipboard automatically." },
    { icon: Languages, title: "Transliterate", description: "Converts your entire typed text from English phonetics to native script in one tap." },
    { icon: ListOrdered, title: "Bullets", description: "Any paragraph → clean bullet points. Perfect for WhatsApp summaries, meeting notes, and to-do lists." },
    { icon: MessageSquare, title: "Auto Reply", description: "Paste the last received message → get a smart, contextual reply ready to send." }
  ];

  const proAIModes = [
    { icon: Smartphone, title: "Create Post", description: "Tell it your topic → get a ready-to-post social media caption for Instagram, LinkedIn, or Facebook." },
    { icon: Film, title: "Reel Caption", description: "Generates hooks and hashtags optimised for Indian content creators on Reels and Shorts." },
    { icon: PartyPopper, title: "Festival Post", description: "Festival name + your offer → branded Diwali / Eid / Pongal greeting post. Ready in 3 seconds." },
    { icon: Briefcase, title: "WhatsApp Business", description: "Professional promo messages, customer replies, and follow-ups for 63M Indian SMB owners." },
    { icon: Mail, title: "Reply Email", description: "Paste a received email → get a complete professional reply drafted and ready in seconds." },
    { icon: Target, title: "Lead Message", description: "Set up your business profile once. Pick a service. Get a personalised outreach message for sales leads." }
  ];

  const languages = [
    { script: "த", name: "Tamil", code: "ta" },
    { script: "हि", name: "Hindi", code: "hi" },
    { script: "తె", name: "Telugu", code: "te" },
    { script: "বা", name: "Bengali", code: "bn" },
    { script: "ಕ", name: "Kannada", code: "kn" },
    { script: "മ", name: "Malayalam", code: "ml" },
    { script: "ਪ", name: "Punjabi", code: "pa" },
    { script: "ગ", name: "Gujarati", code: "gu" },
    { script: "म", name: "Marathi", code: "mr" },
    { script: "অ", name: "Assamese", code: "as" },
    { script: "ଓ", name: "Odia", code: "or" },
    { script: "اُ", name: "Urdu", code: "ur" }
  ];

  const smartFeatures = [
    { icon: HelpCircle, title: "Smart Punctuation", description: "Asking a question? TypeIt4U shows ? . ! in the prediction bar before you even type them." },
    { icon: Calculator, title: "Number Units (Indian)", description: "Type \"100\" → get \"100kg\", \"100ml\". Type \"100000\" → get \"1 Lakh\". Native Indian number formatting." },
    { icon: Sparkles, title: "Inline Emoji Suggestion", description: "Type \"happy\" → see 😊 appear. TypeIt4U learns which emoji you use most per app." },
    { icon: Smartphone, title: "Phone Number Formatter", description: "Type 10 digits → get \"98765 43210\", \"+91-98765-43210\" as instant chips." },
    { icon: AtSign, title: "Email & URL Shorthands", description: "Type \"@g\" → \"@gmail.com\". Type \"www\" → \"www.\". Complete domains in one tap." },
    { icon: MessageSquare, title: "Smart Greetings", description: "Open a new chat? TypeIt4U suggests \"Hi\", \"Hello\", \"Dear Sir\", \"Good morning\" — before you've typed three letters." },
    { icon: Briefcase, title: "Professional Word Pairs", description: "Type \"kindly\" → get \"note that\", \"share the update\". Type \"as per\" → \"our discussion\", \"your request\"." },
    { icon: Quote, title: "Smart Bracket Pairing", description: "Type \" or ( or [ → auto-inserts the closing pair and positions your cursor inside." }
  ];

  const userTypes = [
    {
      icon: ShoppingCart,
      title: "SMB Owners",
      features: ["Festival posts in 3 seconds", "WhatsApp Biz messages instantly", "Lead outreach per service", "No copy-paste between apps", "Type in your language"]
    },
    {
      icon: Video,
      title: "Content Creators",
      features: ["Reel captions with hooks", "Instagram post generator", "Bilingual content creation", "Festival themed posts", "Hashtags for India"]
    },
    {
      icon: Briefcase,
      title: "Professionals",
      features: ["Email replies in seconds", "Professional phrase shortcuts", "Grammar auto-fix", "Math in text fields", "Undo mistakes instantly"]
    },
    {
      icon: Home,
      title: "Everyday India",
      features: ["Type in mother tongue", "Festival greetings ready", "Share location with one tap", "Scan text from anywhere", "Voice input built in"]
    }
  ];

  const comparison = [
    { feature: "All 22 Scheduled Indian Languages", typeit: true, defaultKb: "partial", otherKb: "partial" },
    { feature: "Bilingual Auto-Detection", typeit: true, defaultKb: false, otherKb: false },
    { feature: "Indian Grammar Auto-Corrections", typeit: "385 rules", defaultKb: false, otherKb: "basic" },
    { feature: "Built-in AI Assistant", typeit: "13 modes", defaultKb: "1", otherKb: false },
    { feature: "Festival Greeting Calendar", typeit: "14 festivals", defaultKb: false, otherKb: false },
    { feature: "Built-in Calculator", typeit: true, defaultKb: false, otherKb: false },
    { feature: "Transliterate Whole Field", typeit: true, defaultKb: false, otherKb: "basic" },
    { feature: "OCR Scanner + GPS + Voice", typeit: "All 3", defaultKb: false, otherKb: false },
    { feature: "Indian Number Format (Lakh/Crore)", typeit: true, defaultKb: false, otherKb: false },
    { feature: "Swipe / Glide Typing", typeit: true, defaultKb: true, otherKb: true },
    { feature: "Your Data Stays on Phone", typeit: true, defaultKb: false, otherKb: "partial" },
    { feature: "Made for India", typeit: "100%", defaultKb: false, otherKb: "partial" }
  ];

  const renderComparisonCell = (value) => {
    if (value === true) return <Check className="w-5 h-5 text-green-600 mx-auto" />;
    if (value === false) return <X className="w-5 h-5 text-red-400 mx-auto" />;
    if (value === "partial" || value === "basic") return <Minus className="w-5 h-5 text-yellow-500 mx-auto" />;
    return <span className="text-sm font-medium text-green-600">{value}</span>;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-orange-50 via-white to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full mb-6">
              <span>🇮🇳</span>
              <span className="text-sm font-medium">India's AI Keyboard</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{ color: '#0A0A0A' }}>
              <span className="italic">Type Less.</span> Do More.
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              The only keyboard built from the ground up for Indian users — 22 languages, 13 AI writing modes, smart festival greetings, swipe typing, and an intelligence layer that understands how India types, talks, and does business.
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              {[
                { value: "22", label: "Indian Languages" },
                { value: "13", label: "AI Modes" },
                { value: "385", label: "Auto-Corrections" },
                { value: "14", label: "Festival Greetings" }
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-xl px-6 py-4 shadow-sm border border-gray-100">
                  <div className="text-2xl font-bold" style={{ color: '#FF6B9D' }}>{stat.value}</div>
                  <div className="text-xs text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
            
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                color: 'white',
                borderRadius: '26px',
                padding: '18px 32px',
                fontSize: '16px',
                fontWeight: 500,
                boxShadow: '0 4px 20px rgba(255, 107, 157, 0.3)'
              }}
            >
              <Download className="w-5 h-5" />
              Download on Play Store
            </a>
          </motion.div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-orange-600 mb-2 block">Core Features</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Everything Indian users need in a keyboard — built right.
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              TypeIt4U is built from scratch for Indian users. Not adapted. Not localised. Built.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.05 }}
                  className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-xl transition-all relative"
                >
                  <div className={`absolute top-4 right-4 px-2 py-1 text-xs font-bold rounded-full ${
                    feature.badge === 'EXCLUSIVE' ? 'bg-pink-100 text-pink-600' :
                    feature.badge === 'NEW 2025' ? 'bg-orange-100 text-orange-600' :
                    'bg-green-100 text-green-600'
                  }`}>
                    {feature.badge}
                  </div>
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 140, 66, 0.1) 0%, rgba(255, 107, 157, 0.1) 100%)'
                    }}
                  >
                    <Icon className="w-6 h-6" style={{ color: '#FF6B9D' }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#0A0A0A' }}>
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* KeyMitra AI Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-orange-600 mb-2 block">KeyMitra AI</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              13 AI modes. <span className="italic">Zero app switching.</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Tap a button. Get a result. Stay in your app. The world's most feature-rich keyboard AI — built for India.
            </p>
          </motion.div>

          {/* Free AI Modes */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-6 text-center" style={{ color: '#0A0A0A' }}>
              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm mr-2">FREE</span>
              For Everyone
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {freeAIModes.map((mode, index) => {
                const Icon = mode.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ ...springConfig, delay: index * 0.05 }}
                    className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-lg transition-all"
                  >
                    <Icon className="w-8 h-8 mb-3" style={{ color: '#FF6B9D' }} />
                    <h4 className="font-semibold mb-1" style={{ color: '#0A0A0A' }}>{mode.title}</h4>
                    <p className="text-xs text-gray-600">{mode.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Pro AI Modes */}
          <div>
            <h3 className="text-xl font-semibold mb-6 text-center" style={{ color: '#0A0A0A' }}>
              <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm mr-2">PRO</span>
              ₹99/month · ₹199 one-time
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {proAIModes.map((mode, index) => {
                const Icon = mode.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ ...springConfig, delay: index * 0.05 }}
                    className="bg-white rounded-xl p-5 border-2 border-orange-200 hover:shadow-lg transition-all"
                  >
                    <Icon className="w-8 h-8 mb-3" style={{ color: '#FF8C42' }} />
                    <h4 className="font-semibold mb-1" style={{ color: '#0A0A0A' }}>{mode.title}</h4>
                    <p className="text-xs text-gray-600">{mode.description}</p>
                  </motion.div>
                );
              })}
            </div>
            <p className="text-center text-sm text-gray-600 mt-6">
              All Pro modes respect your <strong>AI Tone</strong> setting — Professional, Casual, Formal, or Friendly.
            </p>
          </div>
        </div>
      </section>

      {/* Language Coverage */}
      <section className="py-20 bg-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-orange-600 mb-2 block">Language Coverage</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Every Indian language. <span className="italic">One keyboard.</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              TypeIt4U is the only keyboard that covers all 22 constitutionally scheduled Indian languages.
            </p>
          </motion.div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4 max-w-4xl mx-auto mb-10">
            {languages.map((lang, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.03 }}
                className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-xl p-4 text-center border border-orange-100"
              >
                <div className="text-2xl font-bold mb-1" style={{ color: '#FF6B9D' }}>{lang.script}</div>
                <div className="text-sm font-medium text-gray-900">{lang.name}</div>
                <div className="text-xs text-gray-500">{lang.code}</div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-orange-100 to-pink-100 rounded-xl p-6 border border-orange-200">
              <p className="text-sm text-gray-700 mb-2">
                <strong>+ 10 more:</strong> Sanskrit, Kashmiri, Konkani, Maithili, Manipuri, Bodo, Dogri, Nepali, Santali, Sindhi
              </p>
              <p className="text-sm font-medium" style={{ color: '#FF6B9D' }}>
                ✓ Bilingual Auto-Detection — Unique to TypeIt4U
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Intelligence */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-orange-600 mb-2 block">Smart Intelligence</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Your keyboard that <span className="italic">understands India.</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              TypeIt4U's prediction bar is not just word suggestions. It reads context and surfaces what you actually need.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {smartFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.05 }}
                  className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-lg transition-all"
                >
                  <Icon className="w-8 h-8 mb-3" style={{ color: '#FF6B9D' }} />
                  <h4 className="font-semibold mb-1 text-sm" style={{ color: '#0A0A0A' }}>{feature.title}</h4>
                  <p className="text-xs text-gray-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 bg-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-orange-600 mb-2 block">Who It's For</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Built for the way <span className="italic">India works.</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Not a global product adapted for India. A product built for specific Indian use cases.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {userTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-6 border border-orange-100"
                >
                  <Icon className="w-10 h-10 mb-4" style={{ color: '#FF6B9D' }} />
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#0A0A0A' }}>{type.title}</h3>
                  <ul className="space-y-2">
                    {type.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#FF6B9D' }} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <span className="text-sm font-medium text-orange-600 mb-2 block">How We Compare</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              TypeIt4U wins <span className="italic">13 out of 14.</span>
            </h2>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-gradient-to-r from-orange-100 to-pink-100">
                  <th className="text-left p-4 font-semibold text-sm">Feature</th>
                  <th className="text-center p-4 font-semibold text-sm" style={{ color: '#FF6B9D' }}>TypeIt4U</th>
                  <th className="text-center p-4 font-semibold text-sm text-gray-600">Default Keyboards</th>
                  <th className="text-center p-4 font-semibold text-sm text-gray-600">Other Keyboards</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-4 text-sm text-gray-800">{row.feature}</td>
                    <td className="p-4 text-center">{renderComparisonCell(row.typeit)}</td>
                    <td className="p-4 text-center">{renderComparisonCell(row.defaultKb)}</td>
                    <td className="p-4 text-center">{renderComparisonCell(row.otherKb)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              The keyboard India was waiting for.
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-8">
              22 languages. 13 AI modes. 385 corrections. Festival greetings. Calculator. Made in Chennai, Tamil Nadu.
            </p>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                color: 'white',
                borderRadius: '26px',
                padding: '18px 32px',
                fontSize: '16px',
                fontWeight: 500,
                boxShadow: '0 4px 20px rgba(255, 107, 157, 0.3)'
              }}
            >
              <Download className="w-5 h-5" />
              Download on Play Store
            </a>
            <p className="text-sm text-gray-500 mt-6">
              TYPE LESS · DO MORE · MADE IN INDIA
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturesPage;
